import { ref, watch, type MaybeRefOrGetter, toValue } from 'vue'

import { normalizeItemPath, normalizeMenuPath } from '@/stores/menu'
import type { MenuItem } from '@/types/menu'

/** True when the page at `path` lives somewhere below `item`. */
export function branchContainsPath(item: MenuItem, path: string): boolean {
  const target = normalizeMenuPath(path)
  const walk = (items: MenuItem[]): boolean =>
    items.some((child) => normalizeItemPath(child.path) === target || walk(child.children ?? []))
  return walk(item.children ?? [])
}

/**
 * One open sub-menu at a time among a set of sibling entries: opening one
 * closes whichever sibling was open. Each level runs its own — the sidebar for
 * the top entries, every category for its children — so opening a sub-menu
 * inside "Gruppi" closes its other sub-menus but leaves "Gruppi" itself open.
 *
 * It starts on the branch holding the current page. The menu often arrives
 * after the sidebar has mounted, so that first pick waits for the entries to be
 * there, and is skipped once the user has opened or closed something.
 */
export function useMenuAccordion(
  items: MaybeRefOrGetter<MenuItem[]>,
  currentPath: MaybeRefOrGetter<string>,
) {
  const openId = ref<number | null>(null)
  let touched = false

  watch(
    () => toValue(items),
    (list) => {
      if (touched || openId.value !== null) return
      const path = toValue(currentPath)
      const active = list.find(
        (item) => (item.children?.length ?? 0) > 0 && branchContainsPath(item, path),
      )
      openId.value = active?.id ?? null
    },
    { immediate: true },
  )

  function toggle(id: number): void {
    touched = true
    openId.value = openId.value === id ? null : id
  }

  function isOpen(id: number): boolean {
    return openId.value === id
  }

  return { openId, toggle, isOpen }
}
